import {useEffect} from 'react';
import {commerceAmount} from '../src/commerce-types';
import type {CommerceEarnings} from '../src/commerce-earnings-types';
import {useHermes} from './lib/store';
import {APP_BASE,Link} from './router';
import {PageHeading,Loading,ErrorNotice,dateTime} from './components';
import './commerce-earnings.css';

const activityLabels={sale:'Sale',refund:'Refund deduction',tax:'Tax',fee:'Fee',payout:'Payout',adjustment:'Adjustment'} as const;
function EarningsBalance({balance}:{balance:CommerceEarnings['balances'][number]}){
  const money=(value:string)=>commerceAmount(value,balance.currency,balance.decimals);
  return <section className="earnings-balance">
    <h3>{balance.currency}{balance.network&&<span> · {balance.network}</span>}</h3>
    <dl className="earnings-totals">
      <div><dt>Net earnings</dt><dd>{money(balance.earnedAtomic)}</dd></div>
      <div><dt>Paid out</dt><dd>{money(balance.paidOutAtomic)}</dd></div>
      <div><dt>Unpaid earnings</dt><dd>{money(balance.unpaidAtomic)}</dd></div>
    </dl>
    <dl className="earnings-breakdown">
      <div><dt>Sales</dt><dd>{balance.sales.toLocaleString()}</dd></div>
      <div><dt>Gross sales</dt><dd>{money(balance.grossAtomic)}</dd></div>
      <div><dt>Refunds</dt><dd>{money(balance.refundedAtomic)}</dd></div>
      {BigInt(balance.refundDueAtomic)>0n&&<div><dt>Refunds still owed</dt><dd>{money(balance.refundDueAtomic)}</dd></div>}
      <div><dt>Tax</dt><dd>{money(balance.taxAtomic)}</dd></div>
      <div><dt>Fees</dt><dd>{money(balance.feesAtomic)}</dd></div>
      {BigInt(balance.adjustmentsAtomic)!==0n&&<div><dt>Adjustments</dt><dd>{money(balance.adjustmentsAtomic)}</dd></div>}
    </dl>
  </section>;
}

export function CommerceEarningsPage(){
  const {state,actions}=useHermes(),resource=state.commerceEarnings,data=resource.data;
  useEffect(()=>{void actions.loadCommerceEarnings().catch(()=>undefined);},[actions,state.session.data?.accountId]);
  return <section className="earnings-page">
    <PageHeading title="Earnings">See sales from your apps and services, and the payments you receive.</PageHeading>
    <Loading active={resource.status==='loading'||resource.status==='idle'}/>
    <ErrorNotice error={resource.error}/>
    {data&&<>
      <div className="earnings-methods">{data.methods.map(method=>{
        const balances=data.balances.filter(balance=>balance.product===method.product);
        return <section className="earnings-method" key={method.product}>
          <header><p className="earnings-label">{method.product==='payments'?'Card payments':'Crypto payments'}</p>
            <h2>{method.name}</h2>
            <p>{method.collectionEnabled?'Payment collection is active.':'Payment collection is not active.'}</p>
          </header>
          {balances.length?balances.map(balance=><EarningsBalance key={`${balance.currency}:${balance.network}:${balance.decimals}`} balance={balance}/>):
            <p className="earnings-empty">No recorded sales yet.</p>}
          <Link className="text-button" href={`${APP_BASE}${method.path}`}>Manage {method.product==='payments'?'payments':'crypto payments'}</Link>
        </section>;
      })}</div>
      <section className="earnings-activity"><header><h2>Payment activity</h2><Link className="text-button" href={`${APP_BASE}/account/invoices`}>View invoices</Link></header>
        {data.activity.length?<div className="earnings-table-scroll"><table><thead><tr><th>Date</th><th>Service</th><th>Type</th><th>Payment method</th><th>Change in unpaid earnings</th></tr></thead>
          <tbody>{data.activity.map(entry=><tr key={entry.id}><td><time dateTime={entry.createdAt}>{dateTime(entry.createdAt)}</time></td>
            <td>{entry.description}<small>Order {entry.orderId}</small></td><td>{activityLabels[entry.category]}</td>
            <td>{entry.product==='payments'?'Card':'Crypto'}{entry.network&&<small>{entry.network}</small>}</td>
            <td>{commerceAmount(entry.changeAtomic,entry.currency,entry.decimals)}</td></tr>)}</tbody></table></div>:
          <p>Confirmed sales, refunds and payouts will appear here.</p>}
        {data.hasMore&&<p>Showing the latest 100 entries. Totals include all recorded transactions.</p>}
      </section>
      <footer className="earnings-notes"><p>Net earnings are sales less completed refunds, refunds still owed, tax and fees, including recorded adjustments. Unpaid earnings are not a payout date or a promise of immediate withdrawal.</p>
        <p>Amounts in different currencies or networks stay separate. Examples and AI credits are not included.</p>
        <p>Updated {dateTime(data.observedAt)}</p><Link className="text-button" href={`${APP_BASE}/billing`}>Usage &amp; Billing</Link></footer>
    </>}
  </section>;
}
